import React, { useEffect, useMemo, useState } from "react";

// 🎨 Iconos (Lucide)
import {
  LuMusic,
  LuTicket,
  LuTrophy,
  LuDrama,
  LuUsers,
  LuSparkles,
  LuLayers,
  LuCircleHelp,
  LuBuilding2,
  LuCalendarDays,
  LuMic
} from "react-icons/lu";

import "../styles/MainContent.css";
import LoadingScreen from "./LoadingScreen";
import { useAuth } from "react-oidc-context";
import Modal from "./Modal";
import { getEventTypes } from "../services/catalog";

// ==========================
// Icon mapping
// ==========================
const ICON_MAP = {
  // Event types (backend)
  CONCIERTO: <LuMusic />,
  FESTIVAL: <LuTicket />,
  DEPORTE: <LuTrophy />,
  TEATRO: <LuDrama />,
  "FAMILIARES Y ESPECIALES": <LuUsers />,
  FAMILIARES: <LuUsers />,
  FAMILIAR: <LuUsers />,          // 👈 alias: la tabla guarda "FAMILIAR" en singular
  ESPECIALES: <LuSparkles />,
  MULTIEVENTOS: <LuLayers />,
  INMUEBLES: <LuBuilding2 />,
  "COMEDY SHOW": <LuMic />,       // 👈 alias: existe en la tabla y no estaba mapeado
  CALENDARIO: <LuCalendarDays />,
  OTRO: <LuCircleHelp />,

  // icon strings (si vienen del backend)
  music: <LuMusic />,
  festival: <LuTicket />,
  sports: <LuTrophy />,
  theater: <LuDrama />,
  family: <LuUsers />,
  special: <LuSparkles />,
  multi: <LuLayers />,
  comedy: <LuMic />,
  calendar: <LuCalendarDays />,
  other: <LuCircleHelp />
};

// ==========================
// UI rules
// ==========================

// ❌ Categorías que NO quieres mostrar
const HIDDEN_TYPES = new Set([
  "DEPORTE",
  "OTRO",
  " ",
  ""
]);

// 🔢 Orden visual fijo
const ORDER = [
  "CALENDARIO",
  "CONCIERTO",
  "FESTIVAL",
  "FAMILIARES Y ESPECIALES",
  "FAMILIAR",
  "INMUEBLES",
  "TEATRO",
  "COMEDY SHOW",
  "MULTIEVENTOS",
  "OTRO",
  "DEPORTE"
];

// 🗓️ Tarjetas que aún no existen en DynamoDB.
// Se inyectan DESPUÉS de los filtros (el filtro de count_events las borraría),
// y solo si el backend no las está mandando ya.
const VIRTUAL_CARDS = [
  {
    event_type: "CALENDARIO",
    label: "Calendario",
    icon: "calendar",
    upload_mode: "direct",     // sin evento ni fecha: se sube el archivo directo
    default_area: "CALENDARIO", // para getUploadSchema; quítalo si no tienes config
    virtual: true
  }
];

const norm = (value) => (value ?? "").toString().trim().toUpperCase();

const MainContent = () => {
  const auth = useAuth();

  const [isModalOpen, setModalOpen] = useState(false);
  const [selectedTypeData, setSelectedTypeData] = useState(null);
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(false);

  // ==========================
  // Load dashboard cards
  // ==========================
  useEffect(() => {
    if (auth.isAuthenticated) {
      setLoading(true);
      getEventTypes()
        .then(data => setCards(data || []))
        .catch(err => console.error("Error cargando dashboard:", err))
        .finally(() => setLoading(false));
    }
  }, [auth.isAuthenticated]);

  const openModal = (typeData) => {
    setSelectedTypeData(typeData);
    setModalOpen(true);
  };

  const closeModal = () => {
    setSelectedTypeData(null);
    setModalOpen(false);
  };

  // ==========================
  // Filters + sorting
  // ==========================
  const visibleCards = useMemo(() => {
    const realCards = (cards || [])
      // 1) Quita basura: sin tipo o sin label (o solo espacios)
      .filter(item => {
        const type = (item?.event_type ?? "").trim();
        const label = (item?.label ?? "").trim();
        return type.length > 0 && label.length > 0;
      })
      // 2) Oculta tipos no deseados
      .filter(item => !HIDDEN_TYPES.has(norm(item.event_type)))
      // 3) Oculta tarjetas con 0 eventos
      .filter(item => (item.count_events ?? 0) > 0);

    // Evita duplicar si el día de mañana el registro ya viene de Dynamo
    const existing = new Set(realCards.map(card => norm(card.event_type)));
    const virtual = VIRTUAL_CARDS.filter(card => !existing.has(norm(card.event_type)));

    // 4) Orden
    return [...realCards, ...virtual].sort((a, b) => {
      const ia = ORDER.indexOf(norm(a.event_type));
      const ib = ORDER.indexOf(norm(b.event_type));
      return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
    });
  }, [cards]);

  if (loading) {
    return <LoadingScreen text="Cargando información..." />;
  }

  return (
    <div className="main-content">
      {/* Grid de Tarjetas */}
      <div className="cards-grid">
        {visibleCards.map((item, index) => {
          const typeKey = norm(item.event_type);
          const icon =
            ICON_MAP[item.icon] ||
            ICON_MAP[typeKey] ||
            <LuCircleHelp />;

          return (
            <button
              key={item.event_type || index}
              className="team-button card-style"
              onClick={() => openModal(item)}
            >
              <div className="card-icon">
                {icon}
              </div>

              <div className="card-info">
                <h3>{item.label}</h3>
                <span className="badge-count">
                  {item.count_events != null
                    ? `${item.count_events} eventos`
                    : "Cargar archivo"}
                </span>
              </div>
            </button>
          );
        })}
      </div>

      {/* Modal */}
      {selectedTypeData && (
        <Modal
          isOpen={isModalOpen}
          onClose={closeModal}
          eventTypeData={selectedTypeData}
        />
      )}
    </div>
  );
};

export default MainContent;
